import { Check, ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/feedback";
import { useToast } from "@/components/ui/toast";
import {
  availability, BREAK_REASONS, lineName, linesText, type AgentStep, type Availability,
  type AvailabilityTone,
} from "@/lib/acd";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAcdMe, useAgentAction } from "@/lib/queries";
import type { AgentState } from "@/lib/types";
import { cn } from "@/lib/utils";

/** The dot beside a state, in the design system's tones. The live floor uses the same ones. */
export const AVAILABILITY_DOT: Record<AvailabilityTone, string> = {
  success: "bg-success-9",
  warning: "bg-warning-9",
  accent: "bg-accent-9",
  neutral: "bg-gray-7",
};

/** The Phone page's larger trigger, filled in the state's tone so it reads from across the page. */
const AVAILABILITY_FILL: Record<AvailabilityTone, string> = {
  success: "border-success-9 bg-success-2 text-success-11 hover:bg-success-4",
  warning: "border-warning-9 bg-warning-2 text-warning-11 hover:bg-warning-4",
  accent: "border-accent-7 bg-accent-3 text-accent-11 hover:bg-accent-4",
  neutral: "border-gray-7 bg-surface text-text hover:bg-surface-hover",
};

/** A state's word on the rail. */
export const AVAILABILITY_TEXT: Record<AvailabilityTone, string> = {
  success: "text-success-11",
  warning: "text-warning-11",
  accent: "text-accent-11",
  neutral: "text-text-muted",
};

/**
 * The rep's ACD state, or null for somebody the ACD has nothing to do with: a phone that staffs no
 * line and is not signed in to one, which is every admin and most supervisors.
 *
 * Only asked of the API for a signed-in account with a phone. The gallery at `/dev/phone` has no
 * account, and seeds the cache instead — which a disabled query still reads.
 */
export function useRepAvailability(): { me: AgentState; view: Availability } | null {
  const { user } = useAuth();
  const { data: me } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  if (!me) return null;
  const view = availability(me);
  if (me.lines.length === 0 && !view.signedIn) return null;
  return { me, view };
}

/**
 * Taking calls: Ready, My calls only, a break and its reason, and sign out. In the phone's header,
 * because the phone is the one surface that follows a rep across every screen (AGENTS.md).
 *
 * The trigger reads the state the ACD recorded, never the item last picked: a pick goes out as a
 * command, and the label changes when the event-tracker says the ACD applied it (`lib/acd.ts`).
 * Items that do not apply stay in the menu, dimmed, with the reason under them, so "why can I not
 * sign out" is answered where the question is asked.
 *
 * Ready reads Ready for every rep. For one with a number of their own on a campaign, the campaign's
 * routing decides whether it takes every call there or only their own callers (ADR-037, amended
 * 2026-09-17), and the phone does not say which.
 *
 * My calls only is the rep's own choice, for one call (ADR-070): Ready, but only for a call to their
 * number or a caller a colleague sends them by name, until they answer one. A rep waiting for a
 * transfer to their number takes it so no other call reaches them first. The ACD ends it, and the
 * phone reads "Ready · My calls only" until then; Ready ends it sooner.
 *
 * `large` is the Phone page's: the first control on the page, with the whole state spelled out
 * and a chevron, where the dock's header has room only for the short word.
 */
export function AvailabilityMenu({ large = false }: { large?: boolean } = {}) {
  const current = useRepAvailability();
  const act = useAgentAction();
  const toast = useToast();
  if (!current) return null;
  const { me, view } = current;

  const run = (steps: AgentStep[]) => {
    act.mutate(
      steps,
      {
        onError: (e) =>
          toast.error(
            "The ACD did not take that",
            e instanceof ApiError ? e.message : "Please try again.",
          ),
      },
    );
  };
  // The lines the rep takes calls on, or would at Ready while signed out. A line they staff and
  // are not signed in to yet is named apart: listed with the others, it reads as joined.
  const lines = [
    ...new Set(me.lines.filter((line) => !view.notYetOn.includes(line)).map(lineName)),
  ];
  const notYetOn = view.notYetOn.length > 0 ? linesText(view.notYetOn) : null;
  const status = notYetOn ? `${view.label}, not yet on ${notYetOn}` : view.label;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Taking calls: ${status}. Change it`}
          className={cn(
            "flex shrink-0 items-center rounded-md border transition-colors",
            large
              ? cn("h-9 gap-2 px-3 text-sm font-semibold shadow-xs", AVAILABILITY_FILL[view.tone])
              : "h-7 gap-1.5 border-border bg-surface px-2 text-xs font-medium text-text hover:bg-surface-hover",
          )}
        >
          {act.isPending ? (
            <Spinner className={large ? "size-3" : "size-2.5"} />
          ) : (
            <span
              className={cn("rounded-full", large ? "size-2.5" : "size-2", AVAILABILITY_DOT[view.tone])}
              aria-hidden
            />
          )}
          {large ? view.label : view.short}
          {large && <ChevronDown className="size-4 opacity-70" aria-hidden />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={large ? "start" : "end"} className="w-64">
        <DropdownMenuLabel>Taking calls</DropdownMenuLabel>
        <p className="px-2 pb-2 text-xs text-text-muted">
          <span className="font-medium text-text">{view.label}</span>
          {lines.length > 0 && ` · ${lines.join(", ")}`}
          {notYetOn && <span className="block">Not signed in to {notYetOn} yet</span>}
        </p>
        <MenuAction
          title="Ready"
          detail={view.ready.detail}
          disabled={view.ready.steps.length === 0}
          checked={view.signedIn && view.state === "available" && !view.ownCallsNext}
          onSelect={() => run(view.ready.steps)}
        />
        <MenuAction
          title="My calls only"
          detail={view.myCalls.detail}
          disabled={view.myCalls.steps.length === 0}
          checked={view.ownCallsNext}
          onSelect={() => run(view.myCalls.steps)}
        />
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Take a break</DropdownMenuLabel>
        {BREAK_REASONS.map((reason) => (
          <DropdownMenuItem
            key={reason.code}
            disabled={view.breakBlocked !== null}
            onSelect={() => run([{ action: "pause", queue: null, code: reason.code }])}
          >
            {reason.label}
          </DropdownMenuItem>
        ))}
        {view.breakBlocked && (
          <p className="px-2 pb-1 text-xs text-text-muted">{view.breakBlocked}</p>
        )}
        <DropdownMenuSeparator />
        <MenuAction
          title="Sign out of your lines"
          detail={view.signOutBlocked ?? "Calls stop coming to you until you are Ready again"}
          disabled={view.signOutBlocked !== null}
          destructive
          onSelect={() => run([{ action: "logout", queue: null }])}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MenuAction({
  title, detail, disabled, destructive = false, checked = false, onSelect,
}: {
  title: string;
  detail: string;
  disabled: boolean;
  destructive?: boolean;
  /** The choice the rep is on now (ADR-037, ADR-070): a tick, and dimmed like any item that does
   *  not apply, its detail saying so. */
  checked?: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem disabled={disabled} destructive={destructive} onSelect={onSelect}>
      <span className="flex min-w-0 flex-1 flex-col">
        <span>{title}</span>
        <span className="text-xs text-text-muted">{detail}</span>
      </span>
      {checked && <Check className="text-accent-11" aria-label="Current" />}
    </DropdownMenuItem>
  );
}
